import type { CalendarOptions } from '@fullcalendar/react'

import { dsText, fontSize, tokenVar } from '../../utils'

/**
 * Default calendar configuration matching the legacy FullCalendar 3.x setup
 */
export const defaultCalendarConfig: Partial<CalendarOptions> = {
  // Height - auto to show full calendar without internal scroll
  height: 'auto',

  // Time grid settings
  slotDuration: '01:00:00', // 1 hour slots
  snapDuration: '00:15:00', // 15 minute snap
  slotMinTime: '00:00:00', // 12:00 AM
  slotMaxTime: '24:00:00', // exclusive end, shows through 11:00 PM

  // Initial view
  initialView: 'timeGridWeek',

  // Default to a fixed date for weekly hours display
  // (weekly hours are typically displayed independent of actual dates)
  initialDate: '2012-01-02',

  // Navigation
  navLinks: false,

  // Selection
  selectable: true,
  selectMirror: true,

  // Editing
  editable: true,
  eventResizableFromStart: true,

  // Display options
  allDaySlot: false,
  nowIndicator: false,

  // Week settings
  firstDay: 1, // Monday

  // Day header format - show only day of week (e.g., "Sun" instead of "Sun 1/1")
  dayHeaderFormat: { weekday: 'short' },

  // Event display
  eventDisplay: 'block',
  eventTimeFormat: {
    hour: 'numeric',
    minute: '2-digit',
    meridiem: false,
  },

  // Limit events per day cell
  dayMaxEvents: true,

  // Style hooks - FullCalendar v7 themes use hashed class names, so custom
  // styling attaches through these stable class options (see calendarStyles)
  dayHeaderClass: 'schedule-cal-day-header',
  slotHeaderClass: 'schedule-cal-slot-header',
  slotLaneClass: 'schedule-cal-slot-lane',
  eventClass: 'schedule-cal-event',
  eventInnerClass: 'schedule-cal-event-inner',
}

/**
 * CSS styles for the calendar (to be applied via a style tag or CSS file)
 */
const body3FontSize = fontSize(dsText.Body3.fontSize)
const timeAxisFontSize = '0.6875rem' /* 11px */

export const calendarStyles = `
.schedule-calendar {
  width: 100%;
  min-width: 0;
  border: 1px solid ${tokenVar('border.default')};
  border-radius: 10px;
  box-sizing: border-box;
  overflow: hidden;
  background-color: ${tokenVar('bg.default')};

  /* FullCalendar v7 classic-theme palette, mapped to semantic tokens */
  --fc-classic-border: ${tokenVar('border.default')};
  --fc-classic-background: ${tokenVar('bg.default')};
  --fc-classic-foreground: ${tokenVar('fg.default')};
  --fc-classic-muted-foreground: ${tokenVar('fg.muted')};
  --fc-classic-highlight: color-mix(in srgb, ${tokenVar('brand.subtle')} 40%, transparent);
  --fc-classic-today: transparent;
}

/* Header cells (day names). The v7 skeleton applies utility classes with
   !important (e.g. padding: 0 !important), so contested props need !important */
.schedule-calendar .schedule-cal-day-header {
  background-color: ${tokenVar('bg.subtle')};
  color: ${tokenVar('fg.default')};
  font-family: ${dsText.Body1.fontFamily};
  font-weight: ${dsText.Body1_Bold.fontWeight};
  font-size: ${fontSize(dsText.Body1.fontSize)};
  line-height: ${dsText.Body1.lineHeight};
  padding: 10px 0 !important;
}

/* Time axis. FullCalendar sizes the axis column by measuring the label's
   inner wrapper, so the min-width belongs there (56px + 8px cell padding
   = 64px column) */
.schedule-calendar .schedule-cal-slot-header {
  color: ${tokenVar('fg.muted')};
  font-family: ${dsText.Body2.fontFamily};
  font-size: ${timeAxisFontSize};
  line-height: ${dsText.Body2.lineHeight};
  padding: 0 4px !important;
  justify-content: center !important;
  white-space: nowrap;
}

.schedule-calendar .schedule-cal-slot-header > * {
  min-width: 56px !important;
  display: flex !important;
  justify-content: center !important;
}

/* Grid lines */
.schedule-calendar .schedule-cal-slot-lane {
  height: 24px;
}

/* Events styling */
.schedule-calendar .schedule-cal-event {
  cursor: pointer;
  background-color: ${tokenVar('brand.subtle')};
  border-color: ${tokenVar('brand.focusRing')};
  color: ${tokenVar('brand.fg')};
  border-radius: 6px;
  box-shadow: none;
}

.schedule-calendar .schedule-cal-event:hover {
  background-color: ${tokenVar('brand.muted')};
}

.schedule-calendar .schedule-cal-event-inner {
  padding: 4px 6px;
  font-family: ${dsText.Body3.fontFamily};
  font-size: ${body3FontSize};
  line-height: ${dsText.Body3.lineHeight};
}

.schedule-calendar .schedule-cal-event .event-delete {
  position: absolute;
  top: 2px;
  right: 4px;
  cursor: pointer;
  font-weight: bold;
  z-index: 3;
  opacity: 0.8;
}

.schedule-calendar .schedule-cal-event .event-delete:hover {
  opacity: 1;
}
`
